
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Activity, ArrowUpRight, ArrowDownLeft, Shield, FileText } from 'lucide-react';
import { Card } from '../../ui/Card';
import { ViewState } from '../../../types';

const CompactAction = ({ icon: Icon, label, onClick }: any) => (
    <button 
        onClick={onClick}
        className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 px-3 py-4 sm:px-4 sm:py-3 rounded-sm bg-white dark:bg-white/5 border border-stone-200 dark:border-white/10 hover:border-nobel-gold/50 transition-all group shrink-0 justify-center shadow-sm active:scale-95"
    >
        <div className="p-1.5 rounded-sm bg-stone-100 dark:bg-white/10 shrink-0">
            <Icon size={18} className="text-stone-600 dark:text-stone-400 group-hover:text-nobel-gold transition-colors" />
        </div>
        <span className="text-[10px] sm:text-[11px] font-black text-stone-800 dark:text-stone-300 uppercase tracking-widest">{label}</span>
    </button>
);

interface ActionStripProps {
    portfolioValue: number;
    title: string;
    onNavigate: (view: ViewState) => void;
    onSend: () => void;
    onReceive: () => void;
    onAudit: () => void;
}

export const ActionStrip: React.FC<ActionStripProps> = ({ 
    portfolioValue, 
    title, 
    onNavigate, 
    onSend, 
    onReceive, 
    onAudit 
}) => {
    return (
        <Card noPadding className="bg-white dark:bg-[#0F1014] p-6 sm:p-8 flex flex-col items-center justify-center gap-8 shadow-smooth transition-all duration-300 rounded-sm overflow-hidden text-center border-stone-200 dark:border-white/10">
            {/* Massive Hero Revenue for Mobile */}
            <div className="space-y-2">
                <h2 className="text-[10px] sm:text-[12px] font-black text-stone-400 dark:text-stone-500 uppercase tracking-[0.4em]">{title}</h2>
                <div className="text-5xl sm:text-6xl md:text-7xl font-serif font-black text-[#0A0B0F] dark:text-white leading-none tracking-tighter">
                    $870,84
                </div>
                <div className="flex items-center justify-center gap-2 mt-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                    <span className="text-[9px] font-mono font-bold text-emerald-600 uppercase tracking-widest">Available Balance</span>
                </div>
            </div>

            {/* Actions Grid - 2x2 on Mobile, Flex on Desktop */}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-3 w-full max-w-2xl justify-center">
                <CompactAction icon={ArrowUpRight} label="SEND" onClick={onSend} />
                <CompactAction icon={ArrowDownLeft} label="RECEIVE" onClick={onReceive} />
                <CompactAction icon={Shield} label="CONTRACTS" onClick={() => onNavigate('escrow')} />
                <CompactAction icon={FileText} label="VIEW HISTORY" onClick={onAudit} />
            </div>
        </Card>
    );
};
